import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  Divider,
  Box,
  Tabs,
  Tab,
  Chip,
  useTheme,
  Menu,
  MenuItem
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ProtecaoAcesso from '../components/ProtecaoAcesso';
import LoadingIndicator from '../components/LoadingIndicator';
import IndiceFavorabilidade from '../components/dashboard/IndiceFavorabilidade';
import SwotRadarChart from '../components/SwotRadarChart';
import PrimaryButton from '../components/PrimaryButton';
import InfoPopover from '../components/InfoPopover';
import { useNotificador } from '../utils/notificador';
import {
  PlannedActionsCard,
  NotStartedActionsCard,
  InProgressActionsCard,
  CompletedActionsCard,
  CancelledActionsCard,
  AcoesPorAreaChart,
  SwotHistogramChart,
  InsightsCard,
  GanttChart
} from '../components/dashboard';
import OKRList from '../components/OKRList';
import PLRList from '../components/PLRList';

import ReactPlayer from 'react-player';
import { Info, TrendingUp, Assessment, Dashboard as DashboardIcon, Map, AttachMoney, Analytics, InfoOutlined, PlayCircle } from '@mui/icons-material';
import BasicModal from '../components/modais/BasicModal';
import MapaEstrategico from './MapaEstrategico';
import TabelaInvestimento from '../components/TabelaInvestimento';
import { empresaService } from '../services/EmpresaService';
import Empresa from '../types/negocio/Empresa';

const Dashboard: React.FC = () => {
  const authContext = useAuth();
  const navigate = useNavigate();
  const [loading] = useState<boolean>(false);
  const { enviarNotificacao, tipos } = useNotificador();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string>('');
  const theme = useTheme();

  const enviarNotificacaoTeste = () => {
    enviarNotificacao(
      'Teste de Notificação',
      'Esta é uma notificação de teste enviada pelo Dashboard',
      tipos.INFO,
      '/tw/notificacoes',
      'Ver notificações'
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleVideoSelect = (videoUrl: string, videoTitle: string) => {
    setCurrentVideoUrl(videoUrl);
    setCurrentVideoTitle(`Entenda a aba Dashboard - ${videoTitle}`);
    setIsVideoModalOpen(true);
    handleMenuClose();
  };

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const empresaData = await empresaService.getEmpresa();
        setEmpresa(empresaData);
      } catch (error) {
        console.error('Erro ao buscar dados da empresa:', error);
      }
    };

    if (authContext.user) {
      fetchEmpresa();
    }
  }, [authContext.user]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return renderDashboardContent();
      case 1:
        return renderInsightsContent();
      case 2:
        return <MapaEstrategico />
      case 3:
        return <TabelaInvestimento />
      default:
        return renderDashboardContent();
    }
  };

  const renderInsightsContent = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography 
          variant="h5" 
          fontWeight="medium"
          sx={{ 
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          Insights Estratégicos
        </Typography>
        <Box sx={{ mb: 2 }}>
          <InsightsCard />
        </Box>
      </Grid>
    </Grid>
  );

  const renderDashboardContent = () => (
    <Grid container spacing={2}>
      {/* Action Statistics Cards - Layout compacto */}
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <PlannedActionsCard />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <NotStartedActionsCard />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <InProgressActionsCard />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
            <CompletedActionsCard />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
            <CancelledActionsCard />
          </Grid>
        </Grid>
      </Grid>

      {/* Analytics Section - Grid melhorado */}
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2}>
          {/* Três cards na mesma linha */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 2,
                height: 300,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body1" 
                  fontWeight="600" 
                  gutterBottom 
                  align="center"
                  sx={{ color: theme.palette.text.primary, mb: 0.5, fontSize: '0.95rem' }}
                >
                  Índice de Favorabilidade
                </Typography>
                <InfoPopover
                  title="Índice de Favorabilidade"
                  description="O índice mede a proporção entre fatores positivos (Pontos Fortes + Oportunidades) e negativos (Pontos Fracos + Ameaças) do seu negócio. Legenda: 0-20% = Crítico, 40-60% = Equilibrado, 80-100% = Excelente."
                  placement="top"
                >
                  <InfoOutlined 
                    sx={{ 
                      fontSize: 16, 
                      color: theme.palette.text.secondary,
                      cursor: 'help',
                      mb: 0.5
                    }} 
                  />
                </InfoPopover>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  overflow: 'hidden',
                  height: 'calc(100% - 30px)',
                  '& > div': {
                    transform: { xs: 'scale(0.6)', sm: 'scale(0.65)', md: 'scale(0.6)', lg: 'scale(0.58)', xl: 'scale(0.7)' },
                    transformOrigin: 'center center'
                  }
                }}
              >
                <IndiceFavorabilidade />
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 2,
                height: 300,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body1" 
                  fontWeight="600" 
                  gutterBottom 
                  align="center"
                  sx={{ color: theme.palette.text.primary, mb: 0.5, fontSize: '0.95rem' }}
                >
                  Análise SWOT - Radar
                </Typography>
                <InfoPopover
                  title="Radar SWOT"
                  description="Visualização das pontuações totais da análise SWOT do seu negócio. Dados: Pontos Fortes e Pontos Fracos, Oportunidades e Ameaças. Cada eixo representa a soma das pontuações cadastradas em cada categoria."
                  placement="top"
                >
                  <InfoOutlined 
                    sx={{ 
                      fontSize: 16, 
                      color: theme.palette.text.secondary,
                      cursor: 'help',
                      mb: 0.5
                    }} 
                  />
                </InfoPopover>
              </Box>
              <Box sx={{ 
                height: 'calc(100% - 30px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                '& > div': {
                  transform: { xs: 'scale(0.65)', sm: 'scale(0.75)', md: 'scale(0.85)', lg: 'scale(0.9)' },
                  transformOrigin: 'center center'
                }
              }}>
                <SwotRadarChart />
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 2,
                height: 300,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body1" 
                  fontWeight="600" 
                  gutterBottom 
                  align="center"
                  sx={{ color: theme.palette.text.primary, mb: 0.5, fontSize: '0.95rem' }}
                >
                  Ações por Área
                </Typography>
                <InfoPopover
                  title="Distribuição de Ações"
                  description="Mostra a quantidade de ações do Plano de Ação distribuídas por área funcional da empresa. Dados extraídos do campo 'Área' de cada ação cadastrada. Útil para identificar quais áreas têm mais iniciativas estratégicas em andamento."
                  placement="top"
                >
                  <InfoOutlined 
                    sx={{ 
                      fontSize: 16, 
                      color: theme.palette.text.secondary,
                      cursor: 'help',
                      mb: 0.5
                    }} 
                  />
                </InfoPopover>
              </Box>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden'
              }}>
                <AcoesPorAreaChart />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Gráfico de Gantt - Cronograma das Ações */}
      <Grid size={{ xs: 12 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4]
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
            <Typography 
              variant="body1" 
              fontWeight="600" 
              align="center"
              sx={{ color: theme.palette.text.primary, fontSize: '0.95rem' }}
            >
              Cronograma de Ações (Gantt)
            </Typography>
            <InfoPopover
              title="Gráfico de Gantt"
              description="Visualização do cronograma das ações do Plano de Ação. Cada barra representa uma ação, mostrando seu período de execução (data de início até prazo de conclusão). A linha tracejada vermelha indica o dia atual. Cores indicam o status: cinza (não iniciada), azul (em andamento), verde (finalizada), vermelho (cancelada), laranja (atrasada)."
              placement="top"
            >
              <InfoOutlined 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.text.secondary,
                  cursor: 'help'
                }} 
              />
            </InfoPopover>
          </Box>
          <GanttChart />
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <ProtecaoAcesso tela="dashboard">
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Grid container spacing={2}>
        {/* Header Section - Redesenhado */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40%',
                height: '100%',
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}20 0%, transparent 70%)`,
                borderRadius: '50% 0 0 50%'
              }
            }}
          >
            {/* Cabeçalho principal */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                position: 'relative',
                zIndex: 1
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography 
                    variant="h3" 
                    fontWeight="700"
                    sx={{ 
                      background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      fontSize: { xs: '1rem', md: '2rem' }
                    }}
                  >
                    Bem-Vindo, {authContext.user?.name}!
                  </Typography>
                  <PrimaryButton
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={handleMenuClick}
                  >
                    <PlayCircle sx={{ fontSize: 18 }} />
                  </PrimaryButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem onClick={() => handleVideoSelect('https://youtu.be/OkmGKM7ZtJA', 'Tutorial')}>
                      Tutorial
                    </MenuItem>
                    <MenuItem onClick={() => handleVideoSelect('https://www.youtube.com/watch?v=Dkp7hGWPjgo', 'Conceito')}>
                      Conceito
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>

            {/* Tabs integradas no header */}
            <Box sx={{ mt: 3, position: 'relative', zIndex: 1 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="dashboard tabs"
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minHeight: 42,
                    px: 2.5,
                    '&.Mui-selected': {
                      color: 'white',
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    height: 3,
                    borderRadius: 2
                  },
                  '& .MuiTabs-flexContainer': {
                    gap: 0.5
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab 
                  label="Dashboard" 
                  icon={<DashboardIcon />} 
                  iconPosition="start"
                />
                <Tab 
                  label="Insights" 
                  icon={<Analytics />} 
                  iconPosition="start"
                />
                <Tab 
                  label="Mapa Estratégico" 
                  icon={<Map />} 
                  iconPosition="start"
                />
                <Tab 
                  label="Investimento" 
                  icon={<AttachMoney />} 
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          </Paper>
        </Grid>

        {/* Tab Content - Espaçamento otimizado */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ mt: 1.5 }}>
            {loading ? (
              <Paper
                elevation={2}
                sx={{
                  p: 6,
                  borderRadius: 2,
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <LoadingIndicator message="Carregando dashboard..." fullHeight />
              </Paper>
            ) : (
              renderTabContent()
            )}
          </Box>
        </Grid>
      </Grid>
      
      {/* Modal de vídeo */}
      <BasicModal
        title={currentVideoTitle}
        open={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        sizeContent="medium"
        tela='dashboard'
      >
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
          <ReactPlayer
            url={currentVideoUrl}
            width='100%'
            height='100%'
            style={{ position: 'absolute', top: 0, left: 0 }}
            controls
          />
        </Box>
      </BasicModal>
    </Container>
    </ProtecaoAcesso>
  );
};

export default Dashboard;